import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Download, Loader2 } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import { api, eur, errMsg } from "@/lib/api";
import { useMeta } from "@/lib/meta";
import { useSort } from "@/lib/sort";

const MONTHS = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const YEAR_COLORS = ["#94a3b8", "#64748b", "#0A2E5C", "#EDD93F", "#16a34a", "#dc2626"];
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

function presets() {
  const t = new Date(), y = t.getFullYear(), m = t.getMonth();
  return [
    ["month", "Questo mese", iso(new Date(y, m, 1)), iso(t)],
    ["prev", "Mese scorso", iso(new Date(y, m - 1, 1)), iso(new Date(y, m, 0))],
    ["year", "Quest'anno", `${y}-01-01`, iso(t)],
    ["lastyear", "Anno scorso", `${y - 1}-01-01`, `${y - 1}-12-31`],
    ["12m", "Ultimi 12 mesi", iso(new Date(y - 1, m, t.getDate() + 1)), iso(t)],
    ["all", "Tutto", "", ""],
  ];
}

const FIRST_DESC = Object.fromEntries(["orders", "pieces", "gross", "vat", "net", "cost", "ship_net", "ship_cost", "revenue_net", "total_cost", "margin", "margin_pct"].map((k) => [k, "desc"]));
const NUM = ["orders", "pieces", "gross", "vat", "net", "cost", "ship_net", "ship_cost", "revenue_net", "total_cost", "margin", "margin_pct"];
const ACC = { name: (r) => r.sortKey ?? r.label, ...Object.fromEntries(NUM.map((k) => [k, (r) => r[k]])) };
const COLS_BASE = [["orders", "Ordini"], ["pieces", "Pezzi"], ["gross", "Venduto IVA incl."], ["vat", "IVA"], ["net", "Netto prodotti"], ["cost", "Costo prodotti"]];
const COLS_SHIP = [["ship_net", "Sped. incassate (netto)"], ["ship_cost", "Costo trasporto"]];
const COLS_END = [["margin", "Margine netto"], ["margin_pct", "%"]];
const fmt = (k, v) => (k === "orders" || k === "pieces" ? v : k === "margin_pct" ? `${v}%` : eur(v));

function StatTable({ rows, withShip, testid, nameLabel, defaultSort = { key: "margin", dir: "desc" } }) {
  const cols = [...COLS_BASE, ...(withShip ? COLS_SHIP : []), ...COLS_END];
  const { rows: sorted, Th } = useSort(rows, ACC, { ...defaultSort, firstDir: FIRST_DESC });
  const exportCsv = () => {
    const head = [nameLabel, ...cols.map((c) => c[1])];
    const lines = [head, ...sorted.map((r) => [r.label, ...cols.map(([k]) => String(r[k]).replace(".", ","))])];
    const blob = new Blob(["\ufeff" + lines.map((l) => l.map((x) => `"${String(x).replace(/"/g, '""')}"`).join(";")).join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `statistiche-${testid}.csv`;
    a.click();
  };
  return (
    <div className="rounded-xl border bg-white">
      <div className="flex justify-end p-2 border-b">
        <button data-testid={`${testid}-csv-btn`} onClick={exportCsv} className="press h-8 px-3 rounded-md border text-xs font-semibold flex items-center gap-1"><Download className="w-3.5 h-3.5" /> CSV</button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid={`${testid}-table`}>
          <thead className="bg-slate-50 text-left text-slate-500">
            <tr><Th k="name" testid={`${testid}-sort-name`}>{nameLabel}</Th>{cols.map(([k, l]) => <Th key={k} k={k} testid={`${testid}-sort-${k}`} className="text-right">{l}</Th>)}</tr>
          </thead>
          <tbody>
            {sorted.map((r, i) => (
              <tr key={r.key} className="border-t" data-testid={`${testid}-row-${i}`}>
                <td className="p-3 font-semibold max-w-[280px]">{r.label}{r.sub && <div className="text-xs font-normal text-slate-500 font-mono">{r.sub}</div>}{r.has_cost === false && <div className="text-xs font-normal text-amber-700">costo mancante</div>}</td>
                {cols.map(([k]) => <td key={k} className={`p-3 text-right whitespace-nowrap ${k === "margin" ? "font-bold text-brand-navy" : k === "vat" ? "text-slate-500" : ""}`}>{fmt(k, r[k])}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
        {!sorted.length && <p className="p-6 text-sm text-slate-500">Nessun dato nel periodo selezionato.</p>}
      </div>
    </div>
  );
}

function Kpis({ d }) {
  const t = d.total;
  const shop = d.source === "shop";
  const cards = [[shop ? "Timbri prodotti" : "Ordini lavorati", t.orders, "orders"], ["Pezzi", t.pieces, "pieces"], ["Venduto IVA incl.", eur(t.gross + t.ship_net * (1 + d.vat_rate / 100)), "gross"],
    [`IVA ${d.vat_rate}%`, eur(t.vat + t.ship_net * d.vat_rate / 100), "vat"], ["Ricavi netti", eur(t.revenue_net), "revenue"], ["Costo prodotti", eur(t.cost), "cost"],
    [`Trasporto (${t.orders} × ${eur(d.avg_shipping_cost)})`, eur(t.ship_cost), "ship"], ["Margine netto", eur(t.margin), "margin"], ["Margine %", `${t.margin_pct}%`, "margin-pct"]].filter((c) => !(shop && c[2] === "ship"));
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
      {cards.map(([l, v, k]) => (
        <div key={k} className={`rounded-lg border p-4 ${k.startsWith("margin") ? "bg-brand-navy text-white border-brand-navy" : "bg-white"}`}>
          <div className={`text-[11px] font-mono uppercase ${k.startsWith("margin") ? "text-brand-yellow" : "text-slate-500"}`}>{l}</div>
          <div className="text-xl font-bold" data-testid={`stats-kpi-${k}`}>{v}</div>
        </div>
      ))}
    </div>
  );
}

const TABS = [["category", "Per categoria"], ["product", "Per prodotto"], ["month", "Per mese"], ["customer", "Per cliente"]];

function YearCompare({ source }) {
  const [d, setD] = useState(null);
  const [metric, setMetric] = useState("revenue_net");
  useEffect(() => { setD(null); api.get("/admin/stats/years", { params: { source } }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))); }, [source]);
  if (!d) return <p className="text-sm text-slate-500">Caricamento confronto anni…</p>;
  const isMoney = metric !== "orders" && metric !== "pieces";
  const data = MONTHS.map((m, i) => Object.fromEntries([["label", m], ...d.years.map((y) => [y.year, y.months[i]?.[metric] ?? 0])]));
  return (
    <section className="space-y-4" data-testid="stats-years">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div><h2 className="text-2xl font-extrabold uppercase">Confronto negli anni</h2><p className="text-xs text-slate-500">{source === "shop" ? "Tutti i timbri prodotti in negozio" : "Tutti gli ordini lavorati"}, mese per mese, anno su anno.</p></div>
        <select data-testid="stats-years-metric" value={metric} onChange={(e) => setMetric(e.target.value)} className="h-9 px-3 rounded-md border border-slate-200 bg-white text-sm">
          <option value="revenue_net">Ricavi netti</option><option value="margin">Margine netto</option><option value="gross">Venduto IVA incl.</option><option value="orders">{source === "shop" ? "Timbri" : "Ordini"}</option><option value="pieces">Pezzi</option>
        </select>
      </div>
      <div className="rounded-xl border bg-white p-4" data-testid="stats-years-chart">
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} width={55} />
            <Tooltip formatter={(v) => (isMoney ? eur(v) : v)} /><Legend wrapperStyle={{ fontSize: 12 }} />
            {d.years.map((y, i) => <Bar key={y.year} dataKey={y.year} fill={YEAR_COLORS[(i + YEAR_COLORS.length - d.years.length) % YEAR_COLORS.length]} radius={[3, 3, 0, 0]} />)}
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="rounded-xl border bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="stats-years-table">
          <thead className="bg-slate-50 text-left text-[11px] uppercase font-mono text-slate-500">
            <tr><th className="p-3">Anno</th>{MONTHS.map((m) => <th key={m} className="p-3 text-right">{m}</th>)}<th className="p-3 text-right">Totale</th><th className="p-3 text-right">vs anno prec.</th></tr>
          </thead>
          <tbody>
            {d.years.map((y, i) => {
              const prev = d.years[i - 1]?.total?.[metric];
              const cur = y.total[metric];
              const delta = prev ? Math.round(((cur - prev) / Math.abs(prev)) * 1000) / 10 : null;
              return (
                <tr key={y.year} className="border-t" data-testid={`stats-year-row-${y.year}`}>
                  <td className="p-3 font-bold">{y.year}</td>
                  {y.months.map((m, j) => <td key={j} className="p-3 text-right whitespace-nowrap text-xs">{m ? (isMoney ? eur(m[metric]) : m[metric]) : "—"}</td>)}
                  <td className="p-3 text-right font-bold whitespace-nowrap">{isMoney ? eur(cur) : cur}</td>
                  <td className={`p-3 text-right font-semibold ${delta === null ? "text-slate-400" : delta >= 0 ? "text-green-700" : "text-rose-700"}`} data-testid={`stats-year-delta-${y.year}`}>{delta === null ? "—" : `${delta > 0 ? "+" : ""}${delta}%`}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="p-3 text-xs text-slate-500">L'anno in corso è parziale: il confronto sul totale considera solo i mesi trascorsi.</p>
      </div>
    </section>
  );
}

export function AdminStats() {
  const meta = useMeta();
  const P = useMemo(presets, []);
  const [range, setRange] = useState({ preset: "year", from: P[2][2], to: P[2][3] });
  const [category, setCategory] = useState("");
  const [source, setSource] = useState("");
  const shop = source === "shop";
  const [tab, setTab] = useState("category");
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let alive = true;
    setBusy(true);
    api.get("/admin/stats/full", { params: { date_from: range.from, date_to: range.to, category, source } })
      .then((r) => alive && setD(r.data)).catch((e) => toast.error(errMsg(e))).finally(() => alive && setBusy(false));
    return () => { alive = false; };
  }, [range.from, range.to, category, source]);
  const rows = useMemo(() => {
    if (!d) return [];
    if (tab === "category") return d.by_category.map((r) => ({ ...r, key: r.id, label: r.name }));
    if (tab === "product") return d.by_product.map((r) => ({ ...r, key: r.id, label: r.name, sub: r.sku }));
    if (tab === "month") return d.by_month.map((r) => ({ ...r, key: r.month, label: `${MONTHS[+r.month.slice(5) - 1]} ${r.month.slice(0, 4)}`, sortKey: r.month }));
    return d.by_customer.map((r) => ({ ...r, key: r.id, label: r.name, sub: r.email }));
  }, [d, tab]);
  return (
    <div className="space-y-8" data-testid="admin-stats">
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-4xl font-extrabold uppercase">Statistiche</h1>
          {busy && <Loader2 className="w-5 h-5 animate-spin text-brand-navy" />}
        </div>
        <div className="inline-flex rounded-lg border-2 border-brand-navy overflow-hidden" data-testid="stats-source">
          {[["", "Ordini online"], ["shop", "Timbri prodotti in negozio"]].map(([k, l]) => (
            <button key={k || "orders"} data-testid={`stats-source-${k || "orders"}`} onClick={() => { setSource(k); if (k === "shop" && tab === "customer") setTab("category"); }}
              className={`press h-10 px-4 text-sm font-semibold ${source === k ? "bg-brand-navy text-white" : "bg-white text-brand-navy"}`}>{l}</button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 items-center" data-testid="stats-filters">
          {P.map(([k, l, f, t]) => (
            <button key={k} data-testid={`stats-preset-${k}`} onClick={() => setRange({ preset: k, from: f, to: t })}
              className={`press h-9 px-3 rounded-full border text-sm ${range.preset === k ? "bg-brand-navy text-white border-brand-navy" : "bg-white"}`}>{l}</button>
          ))}
          <input data-testid="stats-date-from" type="date" value={range.from} onChange={(e) => setRange({ ...range, preset: "", from: e.target.value })} className="h-9 px-2 rounded-md border border-slate-200 text-sm" aria-label="Dal" />
          <span className="text-slate-400">→</span>
          <input data-testid="stats-date-to" type="date" value={range.to} onChange={(e) => setRange({ ...range, preset: "", to: e.target.value })} className="h-9 px-2 rounded-md border border-slate-200 text-sm" aria-label="Al" />
          <select data-testid="stats-category-filter" value={category} onChange={(e) => setCategory(e.target.value)} className="h-9 px-3 rounded-md border border-slate-200 bg-white text-sm">
            <option value="">Tutte le categorie</option>{meta?.categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        {shop ? (
          <p className="text-xs text-slate-500" data-testid="stats-shop-note">Ogni timbro salvato in Crea timbro conta come venduto al prezzo pubblico del sito del prodotto con la stessa misura (o quello scelto al salvataggio). Costi: acquisto timbro + gommino. Nessun costo di trasporto: sono venduti in negozio. Ricavi e margini al netto IVA ({d?.vat_rate ?? 22}%).</p>
        ) : <p className="text-xs text-slate-500">Ricavi e margini al netto IVA ({d?.vat_rate ?? 22}%), l'IVA è indicata a parte. Costi d'acquisto netti: timbro completo = acquisto timbro + costo gommino. Trasporto: {eur(d?.avg_shipping_cost ?? 6.5)} per ordine lavorato (modificabile in Impostazioni). Per i negozi con pagamento differito vale il prezzo del loro listino riservato.{category && " Con una categoria selezionata spedizioni e trasporto non sono conteggiati."}</p>}
        {shop && d?.unmatched > 0 && <p className="text-xs rounded-md bg-amber-50 border border-amber-300 p-2 text-amber-900" data-testid="stats-shop-unmatched">{d.unmatched} timbri salvati nel periodo hanno una misura che non corrisponde a nessun prodotto a catalogo: non sono conteggiati.</p>}
      </div>
      {d && <Kpis d={d} />}
      {d?.missing_costs.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm flex gap-3" data-testid="stats-missing-costs">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
          <div><b>{d.missing_costs.length} prodotti venduti nel periodo non hanno il prezzo d'acquisto</b>: il loro costo conta come 0 e il margine risulta più alto.
            <div className="text-xs text-slate-600 mt-1">{d.missing_costs.slice(0, 8).map((p) => p.sku || p.name).join(" · ")}{d.missing_costs.length > 8 && " …"}</div>
            <Link to="/admin/prodotti" className="text-brand-navy underline text-xs font-semibold">Inserisci i costi in Prodotti (anche su più prodotti insieme)</Link></div>
        </div>
      )}
      {d && (
        <section className="space-y-3">
          <div className="flex flex-wrap gap-1" role="tablist">
            {TABS.filter(([k]) => !(shop && k === "customer")).map(([k, l]) => <button key={k} role="tab" aria-selected={tab === k} data-testid={`stats-tab-${k}`} onClick={() => setTab(k)} className={`press h-9 px-4 rounded-md text-sm font-semibold ${tab === k ? "bg-brand-yellow text-brand-navy" : "bg-white border"}`}>{l}</button>)}
          </div>
          {tab === "month"
            ? <StatTable key="month" rows={rows} withShip={!category} testid="stats-month" nameLabel="Mese" defaultSort={{ key: "name", dir: "asc" }} />
            : <StatTable key={tab} rows={rows} withShip={tab === "customer" && !category} testid={`stats-${tab}`} nameLabel={{ category: "Categoria", product: "Prodotto", customer: "Cliente" }[tab]} />}
          {tab !== "month" && tab !== "customer" && !category && !shop && <p className="text-xs text-slate-500">Margine per {tab === "category" ? "categoria" : "prodotto"} senza spedizioni: il trasporto ({eur(d.shipping.cost)}) e le spedizioni incassate ({eur(d.shipping.revenue_net)} netti) sono nel totale in alto.</p>}
        </section>
      )}
      <YearCompare source={source} />
    </div>
  );
}
